/**
 * ICS Calendar Export
 *
 * Generates a standard iCalendar (.ics) file from Adulting calendar items.
 * The exported file can be imported into any calendar app (Google Calendar,
 * Apple Calendar, Outlook etc.) or used as a subscribable webcal:// URL
 * if hosted on a public endpoint.
 *
 * No external API credentials required — this runs entirely client-side.
 */

export interface IcsEvent {
  uid: string;
  summary: string;
  description?: string;
  dtstart: string; // ISO date string YYYY-MM-DD
  dtend?: string;  // ISO date string YYYY-MM-DD (defaults to dtstart + 1 day)
  categories?: string;
  url?: string;
}

/**
 * Format a date string as an ICS DATE value (YYYYMMDD — all-day event)
 */
function toIcsDate(isoDate: string): string {
  return isoDate.replace(/-/g, '').slice(0, 8);
}

/**
 * Escape special characters in ICS text fields
 */
function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Fold long ICS lines at 75 octets (RFC 5545 §3.1)
 */
function foldLine(line: string): string {
  if (line.length <= 75) return line;
  const chunks: string[] = [];
  chunks.push(line.slice(0, 75));
  let i = 75;
  while (i < line.length) {
    chunks.push(' ' + line.slice(i, i + 74));
    i += 74;
  }
  return chunks.join('\r\n');
}

/**
 * Build a DTSTAMP value (current UTC timestamp)
 */
function nowStamp(): string {
  return new Date().toISOString().replace(/[-:]/g, '').slice(0, 15) + 'Z';
}

/**
 * Generate a complete ICS calendar string from an array of events
 */
export function generateIcs(events: IcsEvent[], calendarName = 'Adulting'): string {
  const stamp = nowStamp();

  const eventBlocks = events.map(evt => {
    const dtstart = toIcsDate(evt.dtstart);
    const dtend = evt.dtend ? toIcsDate(evt.dtend) : toIcsDate(
      // default: next day
      new Date(new Date(evt.dtstart).getTime() + 86400000).toISOString().slice(0, 10)
    );

    const lines = [
      'BEGIN:VEVENT',
      foldLine(`UID:${escapeIcsText(evt.uid)}`),
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${dtstart}`,
      `DTEND;VALUE=DATE:${dtend}`,
      foldLine(`SUMMARY:${escapeIcsText(evt.summary)}`),
    ];

    if (evt.description) {
      lines.push(foldLine(`DESCRIPTION:${escapeIcsText(evt.description)}`));
    }
    if (evt.categories) {
      lines.push(foldLine(`CATEGORIES:${escapeIcsText(evt.categories)}`));
    }
    if (evt.url) {
      lines.push(foldLine(`URL:${escapeIcsText(evt.url)}`));
    }

    lines.push('END:VEVENT');
    return lines.join('\r\n');
  });

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Adulting//Life Admin Calendar//EN',
    `X-WR-CALNAME:${escapeIcsText(calendarName)}`,
    'X-WR-CALDESC:Your life admin deadlines and reminders from Adulting',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...eventBlocks,
    'END:VCALENDAR',
  ].join('\r\n');
}

/**
 * Trigger a browser download of an ICS file
 */
export function downloadIcs(icsContent: string, filename = 'adulting-calendar.ics'): void {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build a "Add to Google Calendar" URL for a single event
 */
export function googleCalendarUrl(evt: IcsEvent): string {
  const base = 'https://calendar.google.com/calendar/render?action=TEMPLATE';
  const params = new URLSearchParams({
    text: evt.summary,
    dates: `${toIcsDate(evt.dtstart)}/${toIcsDate(evt.dtend ?? evt.dtstart)}`,
    details: evt.description ?? '',
  });
  return `${base}&${params.toString()}`;
}
